import InputDate from '@components/InputDate';
import InputDocuments from '@components/InputDocuments';
import InputField from '@components/InputFieldControlled';
import Text from '@components/Text';
import { Checkbox, Stack } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import Regex from '@constants/Regex';
import { Control, UseFormClearErrors, UseFormSetError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { TalentLanguage, LanguageFormValues } from '../types';

interface CertificateFormProps {
    control: Control<LanguageFormValues>;
    clearErrors: UseFormClearErrors<LanguageFormValues>;
    watch: UseFormWatch<LanguageFormValues>;
    setValue: UseFormSetValue<LanguageFormValues>;
    setError: UseFormSetError<LanguageFormValues>;
    randomKey: number;
    selectedData: TalentLanguage | null;
}

const CertificateForm = ({
    control,
    clearErrors,
    watch,
    setValue,
    setError,
    randomKey,
    selectedData,
}: CertificateFormProps) => {
    const entry = selectedData?.languages?.[0];
    const itemCertificate = !isEmpty(entry?.certificates) ? entry.certificates[0] : null;
    const [isLifetime, setIsLifetime] = useState(!itemCertificate?.endDate);

    return (
        <React.Fragment>
            <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2.5 }}>
                <Stack sx={{ flex: 2 }}>
                    <InputField
                        name="title"
                        control={control}
                        multiline
                        label="Titre de la certification"
                        placeholder="ex : English EF Set"
                        defaultValue={itemCertificate?.title ?? ''}
                        required
                    />
                </Stack>
                <Stack sx={{ flex: 1 }}>
                    <InputField
                        name="obtainedResult"
                        control={control}
                        multiline
                        keyboardType="default"
                        label="Résultat obtenu"
                        placeholder="Ex : 500"
                        defaultValue={itemCertificate?.obtainedResult ?? ''}
                        required
                    />
                </Stack>
            </Stack>
            <InputField
                name="issuer"
                control={control}
                multiline
                label="Nom de l'organisme émetteur"
                placeholder="Ex : English First"
                defaultValue=""
                required
            />
            <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2.5 }}>
                <Stack sx={{ flex: 1 }}>
                    <InputDate
                        viewOnly={false}
                        label="Date d'obtention"
                        placeholder="MM/YYYY"
                        name="startDate"
                        control={control}
                        maxDate={moment().toDate()}
                        required
                        format={'MM/YYYY'}
                        defaultValue={undefined}
                    />
                </Stack>
                <Stack sx={{ flex: 1 }}>
                    <InputDate
                        viewOnly={false}
                        label="Date d'expiration"
                        placeholder="MM/YYYY"
                        name="endDate"
                        control={control}
                        format={'MM/YYYY'}
                        required={!isLifetime}
                        defaultValue={undefined}
                    />
                </Stack>
            </Stack>
            <label
                htmlFor="checkbox-lifetime"
                style={{ marginBottom: 16, display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }}>
                <Checkbox
                    id="checkbox-lifetime"
                    checked={isLifetime && watch('endDate') === null}
                    onChange={e => {
                        if (e.target.checked) {
                            setValue('endDate', null);
                            clearErrors('endDate');
                        } else {
                            setError('endDate', {
                                type: 'required',
                                message: "La date d'expiration est obligatoire si le certificat expire",
                            });
                        }
                        setIsLifetime(e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'lifetime checkbox' }}
                />
                <Text darkGray size={16} error={false}>
                    Ce certificat n'expire jamais
                </Text>
            </label>
            <InputField name="number" control={control} label="Numéro du certificat" />
            <InputField
                name="url"
                control={control}
                rules={{
                    regex: (value: string) => {
                        if (isEmpty(value)) return true;
                        return Regex.URL_REGEX_WITHOUT_PROTOCOL.test(value) || 'Veuillez entrer une URL valide';
                    },
                }}
                label="URL du certificat"
            />
            <InputDocuments
                key={randomKey}
                name="file"
                control={control}
                label="Certificat"
                placeholder="Files"
                file={itemCertificate?.file}
            />
        </React.Fragment>
    );
};

export default CertificateForm;
